<template>
    <div class="points-page-wrapper">
        <div class="points-container">
            <!-- Top Overview Card -->
            <div class="overview-header content-card glass-effect mb-24">
                <Row type="flex" align="middle" :gutter="24">
                    <Col :xs="24" :sm="12" :md="8">
                    <div class="balance-display">
                        <div class="label">当前积分余额</div>
                        <div class="value">
                            <span class="number">{{ balance.total_points }}</span>
                            <Icon type="md-coin" class="coin-icon" />
                        </div>
                        <div class="sub-info" v-if="balance.expiring_points > 0">
                            <Icon type="ios-alert-outline" />
                            {{ balance.expiring_points }} 积分将于 30 天内过期
                        </div>
                    </div>
                    </Col>
                    <Col :xs="24" :sm="12" :md="8">
                    <div class="streak-info">
                        <div class="label">连续签到</div>
                        <div class="streak-count">
                            <span class="number">{{ balance.checkin_streak }}</span>
                            <span class="unit">天</span>
                        </div>
                        <div class="checkin-status">
                            <Tag v-if="balance.has_checked_in_today" color="success">今日已签到</Tag>
                            <Tag v-else color="warning">今日未签到</Tag>
                        </div>
                    </div>
                    </Col>
                    <Col :xs="24" :sm="24" :md="8" class="header-actions">
                    <Button type="primary" size="large" icon="md-checkmark-circle" long
                        :disabled="balance.has_checked_in_today" @click="handleCheckin" class="checkin-btn">
                        {{ balance.has_checked_in_today ? '今日已签到' : '立即签到领积分' }}
                    </Button>
                    <div class="next-milestone" v-if="!balance.has_checked_in_today">
                        连续签到 7/30 天可获额外奖励
                    </div>
                    </Col>
                </Row>
            </div>

            <div class="main-content">
                <div class="content-card glass-effect">
                    <Tabs v-model="activeTab" class="points-tabs">
                        <!-- Transaction History -->
                        <TabPane label="积分流水" name="history" icon="md-list">
                            <Table :columns="historyColumns" :data="historyData" :loading="historyLoading"
                                no-data-text="暂无积分明细" class="history-table"></Table>
                            <div class="pagination-wrapper">
                                <Page :total="historyTotal" :current="historyPage" :page-size="historySize"
                                    @on-change="handlePageChange" size="small" show-total />
                            </div>
                        </TabPane>

                        <!-- Recharge Points -->
                        <TabPane label="积分充值" name="recharge" icon="md-card">
                            <div class="recharge-section">
                                <div class="section-title">选择充值档位 <span class="price-hint">100 积分 = 1 元</span></div>
                                <Row :gutter="16">
                                    <Col v-for="pkg in packages" :key="pkg.amount" :xs="12" :sm="8" :md="6">
                                    <div class="recharge-card"
                                        :class="{ active: selectedPackage?.amount === pkg.amount }"
                                        @click="selectedPackage = pkg">
                                        <div class="points-val">{{ pkg.total }} 积分</div>
                                        <div class="price">¥{{ (pkg.amount / 100).toFixed(2) }}</div>
                                        <div class="bonus-tag" v-if="pkg.bonus > 0">赠 {{ pkg.bonus }}</div>
                                    </div>
                                    </Col>
                                </Row>

                                <div class="recharge-action" v-if="selectedPackage">
                                    <Divider />
                                    <div class="summary">
                                        最终金额：<span class="final-price">¥{{ (selectedPackage.amount / 100).toFixed(2)
                                            }}</span>
                                    </div>
                                    <Button type="primary" size="large" @click="handleRecharge"
                                        :loading="rechargeLoading">
                                        立即充值
                                    </Button>
                                </div>
                            </div>
                        </TabPane>

                        <!-- Invite Friends -->
                        <TabPane label="邀请奖励" name="invite" icon="md-people">
                            <div class="invite-section">
                                <Row :gutter="32">
                                    <Col :xs="24" :md="10">
                                    <div class="invite-card pink-gradient">
                                        <h3>我的邀请码</h3>
                                        <div class="code-box">
                                            <span class="code">{{ inviteInfo.invite_code }}</span>
                                            <Button type="text" icon="md-copy"
                                                @click="copyText(inviteInfo.invite_code)">复制</Button>
                                        </div>
                                        <p class="invite-rule">好友通过您的邀请码注册，您可得 <strong>50 积分</strong>；好友首次付费下载，您可得
                                            <strong>100 积分</strong>。
                                        </p>
                                        <Button type="default" ghost long @click="copyText(inviteLink)">复制邀请链接</Button>
                                    </div>
                                    </Col>
                                    <Col :xs="24" :md="14">
                                    <div class="invite-stats">
                                        <div class="stat-item">
                                            <div class="stat-val">{{ inviteInfo.total_invited }}</div>
                                            <div class="stat-label">已邀请人数</div>
                                        </div>
                                        <div class="stat-item">
                                            <div class="stat-val">{{ inviteInfo.rewarded_pay }}</div>
                                            <div class="stat-label">首充回馈数</div>
                                        </div>
                                    </div>

                                    <Divider orientation="left">邀请记录</Divider>
                                    <Table :columns="inviteColumns" :data="inviteRecords" size="small"
                                        no-data-text="暂无邀请记录"></Table>
                                    </Col>
                                </Row>
                            </div>
                        </TabPane>
                    </Tabs>
                </div>
            </div>
        </div>

        <!-- Recharge QR Modal -->
        <Modal v-model="rechargeModal" title="积分充值支付" footer-hide :mask-closable="false" class="recharge-premium-modal">
            <div class="recharge-qr-container">
                <div class="pay-method-header">
                    <img src="@/assets/weixin_pay_logo.png" class="wx-logo" alt="WeChat Pay" />
                    <span class="pay-text">微信扫码支付</span>
                </div>

                <div class="qr-wrapper">
                    <div class="qr-border">
                        <img :src="rechargeData.qr_url" alt="QR Code" class="qr-img" />
                    </div>
                    <p class="pay-instruction">请使用微信“扫一扫”完成支付</p>
                </div>

                <div class="order-info-card">
                    <div class="info-row">
                        <span class="label">充值项目</span>
                        <span class="value">{{ rechargeData.total_points }} 积分</span>
                    </div>
                    <div class="info-row">
                        <span class="label">应付金额</span>
                        <span class="value amount">¥{{ (rechargeData.amount / 100).toFixed(2) }}</span>
                    </div>
                </div>

                <div class="pay-status-alert">
                    <Icon type="ios-loading" class="is-loading" v-if="!rechargeConfirming" />
                    <Icon type="md-checkmark-circle" v-else />
                    <span>支付成功后系统将自动到账</span>
                </div>

                <div class="modal-footer">
                    <Button type="text" @click="rechargeModal = false">取消支付</Button>
                    <Button type="primary" shape="circle" @click="fetchBalance"
                        :loading="rechargeConfirming">我已支付</Button>
                </div>
            </div>
        </Modal>
    </div>
</template>

<script>
import api from '@/utils/api';

export default {
    name: 'PointsCenter',
    data() {
        return {
            activeTab: 'history',
            balance: {
                total_points: 0,
                frozen_points: 0,
                checkin_streak: 0,
                expiring_points: 0,
                has_checked_in_today: false
            },
            // History
            historyLoading: false,
            historyData: [],
            historyTotal: 0,
            historyPage: 1,
            historySize: 10,
            historyColumns: [
                { title: '日期', key: 'CreatedAt', width: 170, render: (h, { row }) => h('span', this.formatDate(row.CreatedAt)) },
                { title: '类型', key: 'type', width: 120, render: (h, { row }) => this.renderPointType(h, row) },
                { title: '明细', key: 'Remark' },
                {
                    title: '变动',
                    key: 'delta',
                    width: 100,
                    align: 'right',
                    render: (h, { row }) => {
                        const color = row.delta > 0 ? '#19be6b' : '#ed4014';
                        return h('span', { style: { color, fontWeight: 'bold' } }, (row.delta > 0 ? '+' : '') + row.delta);
                    }
                }
            ],
            // Recharge
            packages: [],
            selectedPackage: null,
            rechargeLoading: false,
            rechargeModal: false,
            rechargeData: {},
            rechargeConfirming: false,
            // Invite
            inviteInfo: {
                invite_code: '',
                invite_link: '',
                total_invited: 0,
                rewarded_pay: 0
            },
            inviteRecords: [],
            inviteColumns: [
                { title: '受邀者', key: 'invitee_username' },
                { title: '注册日期', render: (h, { row }) => h('span', this.formatDateSimple(row.created_at)) },
                {
                    title: '首充奖励',
                    align: 'center',
                    render: (h, { row }) => h('Tag', { props: { color: row.first_pay_rewarded ? 'success' : 'default' } }, row.first_pay_rewarded ? '已发放' : '未达成')
                }
            ]
        };
    },
    computed: {
        inviteLink() {
            // Build full URL
            const base = window.location.origin + window.location.pathname.replace(/\/points-center$/, '');
            return base + '/register?invite=' + this.inviteInfo.invite_code;
        }
    },
    mounted() {
        this.fetchBalance();
        this.fetchHistory();
        this.fetchPackages();
        this.fetchInviteInfo();
    },
    methods: {
        async fetchBalance() {
            try {
                const res = await api.get('/api/points/balance');
                this.balance = res.data;
            } catch (e) {
                console.error(e);
            }
        },
        async fetchHistory() {
            this.historyLoading = true;
            try {
                const res = await api.get('/api/points/transactions', {
                    params: { page: this.historyPage, size: this.historySize }
                });
                this.historyData = res.data.records || [];
                this.historyTotal = res.data.total || 0;
            } catch (e) {
                this.$Message.error('获取历史记录失败');
            } finally {
                this.historyLoading = false;
            }
        },
        async fetchPackages() {
            try {
                const res = await api.get('/api/points/recharge/packages');
                this.packages = res.data.packages || [];
                if (this.packages.length > 0) this.selectedPackage = this.packages[1] || this.packages[0];
            } catch (e) {
                console.error(e);
            }
        },
        async fetchInviteInfo() {
            try {
                const res = await api.get('/api/invite/code');
                this.inviteInfo = res.data;
                const recordsRes = await api.get('/api/invite/records');
                this.inviteRecords = recordsRes.data.records || [];
            } catch (e) {
                console.error(e);
            }
        },
        async handleCheckin() {
            try {
                const res = await api.post('/api/points/checkin');
                this.$Notice.success({
                    title: '签到成功',
                    desc: res.data.message
                });
                this.fetchBalance();
                this.fetchHistory();
            } catch (e) {
                this.$Message.error(e.response?.data?.error || '签到失败');
            }
        },
        async handleRecharge() {
            if (!this.selectedPackage) return;
            this.rechargeLoading = true;
            try {
                const res = await api.post('/api/points/recharge', { amount: this.selectedPackage.amount });
                this.rechargeData = res.data;
                this.rechargeModal = true;
                // Start polling check
                this.startRechargeCheck(res.data.out_trade_no);
            } catch (e) {
                this.$Message.error('支付发起失败');
            } finally {
                this.rechargeLoading = false;
            }
        },
        startRechargeCheck(no) {
            const timer = setInterval(async () => {
                if (!this.rechargeModal) {
                    clearInterval(timer);
                    return;
                }
                try {
                    const res = await api.get('/api/points/balance');
                    if (res.data.total_points > this.balance.total_points) {
                        this.$Notice.success({ title: '充值到账', desc: '您的积分余额已更新' });
                        this.balance = res.data;
                        this.rechargeModal = false;
                        this.fetchHistory();
                        clearInterval(timer);
                    }
                } catch (e) { }
            }, 3000);

            // Auto clear after 5 mins
            setTimeout(() => clearInterval(timer), 300000);
        },
        handlePageChange(page) {
            this.historyPage = page;
            this.fetchHistory();
        },
        async copyText(text) {
            if (!navigator.clipboard || !navigator.clipboard.writeText) {
                this.$Message.error('当前浏览器不支持自动复制');
                return;
            }
            try {
                await navigator.clipboard.writeText(text);
                this.$Message.success('已复制到剪贴板');
            } catch (e) {
                this.$Message.error('复制失败，请检查浏览器权限');
            }
        },
        formatDate(val) {
            if (!val) return '-';
            const d = new Date(val);
            return d.toLocaleString();
        },
        formatDateSimple(val) {
            if (!val) return '-';
            // val is unix epoch from backend EXTRACT(EPOCH)
            const d = new Date(val * 1000);
            return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
        },
        renderPointType(h, row) {
            const types = {
                'daily_checkin': { label: '每日签到', color: 'green' },
                'checkin_milestone': { label: '里程碑奖励', color: 'gold' },
                'recharge': { label: '积分充值', color: 'blue' },
                'download': { label: '资源下载', color: 'volcano' },
                'post': { label: '发布帖子', color: 'cyan' },
                'comment': { label: '发表评论', color: 'geekblue' },
                'like': { label: '倍受好评', color: 'magenta' },
                'invite_register': { label: '邀请注册', color: 'purple' },
                'invite_pay': { label: '邀请首充', color: 'pink' },
                'register_gift': { label: '注册礼包', color: 'orange' },
                'feedback': { label: '纠错采案', color: 'primary' }
            };
            const info = types[row.type] || { label: row.type, color: 'default' };
            return h('Tag', { props: { color: info.color } }, info.label);
        }
    }
};
</script>

<style scoped>
.points-page-wrapper {
    min-height: calc(100vh - 64px);
    background: #f8f9fa;
    padding: 40px 0;
}

.points-container {
    max-width: 1000px;
    margin: 0 auto;
    padding: 0 20px;
}

.content-card {
    background: #fff;
    border-radius: 16px;
    padding: 30px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.glass-effect {
    background: rgba(255, 255, 255, 0.8) !important;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.3);
}

.mb-24 {
    margin-bottom: 24px;
}

/* Overview Header */
.balance-display,
.streak-info {
    padding: 10px;
}

.label {
    font-size: 14px;
    color: #808695;
    margin-bottom: 8px;
}

.value {
    display: flex;
    align-items: center;
    gap: 8px;
}

.value .number {
    font-size: 36px;
    font-weight: 800;
    color: #3b82f6;
    font-family: 'Dosis', sans-serif;
}

.coin-icon {
    font-size: 28px;
    color: #ff9900;
}

.sub-info {
    font-size: 12px;
    color: #ed4014;
    margin-top: 4px;
}

.streak-count .number {
    font-size: 32px;
    font-weight: 700;
    color: #1a1a1a;
}

.streak-count .unit {
    font-size: 14px;
    color: #666;
    margin-left: 4px;
}

.checkin-status {
    margin-top: 8px;
}

.header-actions {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

.checkin-btn {
    height: 48px;
    font-size: 16px;
    font-weight: 600;
    border-radius: 24px;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.next-milestone {
    font-size: 12px;
    color: #808695;
    margin-top: 10px;
}

/* Tabs & Sections */
.points-tabs :deep(.ivu-tabs-bar) {
    border-bottom: 2px solid #f8f9fa;
    margin-bottom: 24px;
}

.history-table {
    border-radius: 8px;
    overflow: hidden;
}

.pagination-wrapper {
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;
}

.recharge-section {
    padding: 0 10px;
}

.section-title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 20px;
    color: #1a1a1a;
}

.price-hint {
    font-size: 12px;
    font-weight: normal;
    color: #808695;
    margin-left: 10px;
}

.recharge-card {
    border: 2px solid #f0f2f5;
    border-radius: 12px;
    padding: 24px 16px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s;
    position: relative;
    margin-bottom: 16px;
    background: #fff;
}

.recharge-card:hover {
    border-color: #3b82f6;
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(59, 130, 246, 0.1);
}

.recharge-card.active {
    border-color: #3b82f6;
    background: rgba(59, 130, 246, 0.05);
}

.points-val {
    font-size: 20px;
    font-weight: 700;
    color: #1c2438;
}

.price {
    font-size: 16px;
    color: #3b82f6;
    margin-top: 8px;
    font-weight: 600;
}

.bonus-tag {
    position: absolute;
    top: -10px;
    right: -5px;
    background: linear-gradient(135deg, #ff9d00, #ff4c00);
    color: #fff;
    font-size: 12px;
    padding: 2px 8px;
    border-radius: 10px;
    box-shadow: 0 2px 6px rgba(255, 76, 0, 0.3);
}

.recharge-action {
    text-align: right;
    margin-top: 20px;
}

.confirm-info {
    margin-bottom: 16px;
    font-size: 16px;
}

.final-price {
    font-size: 28px;
    color: #ed4014;
    font-weight: 800;
    margin-left: 8px;
}

/* Invite Section */
.invite-section {
    padding: 10px;
}

.invite-card {
    padding: 30px;
    border-radius: 20px;
    color: #fff;
    text-align: center;
    margin-bottom: 24px;
}

.pink-gradient {
    background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
    box-shadow: 0 8px 20px rgba(245, 87, 108, 0.3);
}

.code-box {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    padding: 12px;
    margin: 20px 0;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
}

.code {
    font-size: 32px;
    font-weight: 800;
    letter-spacing: 4px;
}

.invite-rule {
    font-size: 13px;
    line-height: 1.6;
    margin-bottom: 24px;
    opacity: 0.9;
}

.invite-stats {
    display: flex;
    gap: 20px;
    margin-bottom: 32px;
}

.stat-item {
    flex: 1;
    background: #f8f9fa;
    padding: 20px;
    border-radius: 16px;
    text-align: center;
}

.stat-val {
    font-size: 28px;
    font-weight: 800;
    color: #1c2438;
}

.stat-label {
    font-size: 13px;
    color: #808695;
    margin-top: 4px;
}

/* Recharge QR Modal */
.recharge-qr-container {
    text-align: center;
    padding: 10px 0 0;
}

.pay-method-header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-bottom: 20px;
}

.wx-logo {
    height: 22px;
}

.pay-text {
    font-size: 16px;
    font-weight: 700;
    color: #07c160;
}

.qr-wrapper {
    margin-bottom: 20px;
}

.qr-border {
    display: inline-block;
    padding: 10px;
    background: #fff;
    border: 1px solid #eee;
    border-radius: 12px;
}

.qr-img {
    width: 180px;
    height: 180px;
    display: block;
}

.pay-instruction {
    margin-top: 10px;
    font-size: 13px;
    color: #808695;
}

.order-info-card {
    background: #f8f9fa;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 20px;
}

.info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
}

.info-row .label {
    color: #808695;
    font-size: 13px;
}

.info-row .value {
    font-weight: 600;
    color: #1c2438;
}

.info-row .amount {
    color: #ed4014;
    font-size: 18px;
}

.pay-status-alert {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #19be6b;
    font-size: 13px;
    font-weight: 500;
    background: #f6ffed;
    padding: 10px;
    border-radius: 8px;
    margin-bottom: 10px;
}

.modal-footer {
    margin-top: 20px;
    display: flex;
    justify-content: center;
    gap: 12px;
}

:deep(.recharge-premium-modal .ivu-modal-content) {
    border-radius: 20px;
}

:deep(.recharge-premium-modal .ivu-modal-header-inner) {
    font-weight: 700;
}

.is-loading {
    animation: ani-demo-spin 1s linear infinite;
}

@keyframes ani-demo-spin {
    from {
        transform: rotate(0deg);
    }

    50% {
        transform: rotate(180deg);
    }

    to {
        transform: rotate(360deg);
    }
}
</style>
